import { ICreatePurchase } from "@entities/accounting/lib/types";
import { useGetBrands } from "@entities/brand";
import { useGetBriefs } from "@entities/brief/lib/hooks/useGetBriefs";
import { LEDGER_TYPE } from "@shared/lib/types/briefLedger";
import {
  Button,
  Flex,
  Form,
  Input,
  InputNumber,
  Modal,
  Radio,
  Select,
  Typography,
} from "antd";
import { Check, ShoppingBag } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { useDebounce } from "use-debounce";
import { useCreatePurchase } from "../lib/hooks/useCreatePurchase";
import styles from "./AddPurchaseModal.module.scss";

interface AddPurchaseModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const CATEGORY_OPTIONS = [
  "Reimbursement",
  "Creator cost Increased",
  "Creator cost decreased",
  "Discount",
];

export function AddPurchaseModal({ isOpen, onClose }: AddPurchaseModalProps) {
  const [form] = Form.useForm();
  const [type, setType] = useState<LEDGER_TYPE>(LEDGER_TYPE.Debit);
  const [brandSearch, setBrandSearch] = useState("");
  const [debouncedBrandSearch] = useDebounce(brandSearch, 1000);

  const [campaignSearch, setCampaignSearch] = useState("");
  const [debouncedCampaignSearch] = useDebounce(campaignSearch, 1000);

  const { create, isCreating, isCreated } = useCreatePurchase();
  const { brands, isFetching: areBrandsLoading } = useGetBrands(
    10,
    1,
    debouncedBrandSearch
  );

  const brandOptions = useMemo(
    () => brands?.data?.map((b) => ({ label: b.name, value: b._id })) ?? [],
    [brands]
  );

  const brandId = Form.useWatch("brandId", form);
  const { briefs, isFetching: areBriefsLoading } = useGetBriefs(
    {
      campaignByBrandIds: brandId ? [brandId] : undefined,
      pageNo: 1,
      limit: 10,
      searchQuery: debouncedCampaignSearch,
    },
    !!brandId
  );

  const sellingCost = Form.useWatch("sellingCost", form) || 0;
  const serviceCost = Form.useWatch("serviceCost", form) || 0;
  const gstRate = Form.useWatch("gstRate", form) || 0;

  const subtotal = sellingCost + serviceCost;
  const gstAmount = subtotal * (gstRate / 100);
  const total = subtotal + gstAmount;

  useEffect(() => {
    if (brandId) {
      form.setFieldsValue({ briefId: null });
    }
  }, [brandId, form]);

  useEffect(() => {
    if (isCreated) {
      form.resetFields();
      setType(LEDGER_TYPE.Debit);
      onClose();
    }
  }, [isCreated, form, onClose]);

  const handleFinish = (values: any) => {
    const payload: ICreatePurchase = {
      briefId: values.briefId,
      remarks: values.remarks,
      sellingCost: values.sellingCost,
      serviceCost: values.serviceCost || 0,
      gstRate: values.gstRate,
      type: type,
      category: values.category,
      subType: "Others",
    };
    create(payload);
  };

  const renderHeader = () => (
    <Flex gap={10} align="center" className={styles.headerTitle}>
      <div className={styles.headerIcon}>
        <ShoppingBag size={22} />
      </div>
      <Typography.Text style={{ fontSize: 16 }}>
        Add New Purchase
      </Typography.Text>
    </Flex>
  );

  const renderFooter = () => (
    <>
      <Button onClick={onClose} disabled={isCreating} type="text">
        Cancel
      </Button>
      <Button type="primary" onClick={() => form.submit()} loading={isCreating}>
        Add Purchase
      </Button>
    </>
  );

  return (
    <Modal
      title={renderHeader()}
      open={isOpen}
      onCancel={onClose}
      footer={renderFooter()}
      className={styles.modal}
      width={600}
      centered
      destroyOnClose
    >
      <Form
        form={form}
        layout="vertical"
        onFinish={handleFinish}
        className={styles.form}
        initialValues={{ gstRate: 18 }}
        requiredMark={false}
      >
        <div className={styles.grid}>
          <Form.Item
            name="brandId"
            label={<span className={styles.label}>Brand Name</span>}
            rules={[{ required: true, message: "Please select a brand!" }]}
          >
            <Select
              placeholder="Select Brand"
              loading={areBrandsLoading}
              onSearch={(value) => setBrandSearch(value)}
              searchValue={brandSearch}
              showSearch
              filterOption={false}
            >
              {brands?.data?.map((b) => (
                <Select.Option key={b._id} value={b._id}>
                  {b.name}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item
            name="briefId"
            label={<span className={styles.label}>Campaign Name</span>}
            rules={[{ required: true, message: "Please select a campaign!" }]}
          >
            <Select
              placeholder="Select Campaign"
              disabled={!brandId || areBriefsLoading}
              loading={areBriefsLoading}
              onSearch={(value) => setCampaignSearch(value)}
              searchValue={campaignSearch}
              showSearch
              filterOption={false}
            >
              {briefs?.map((c) => (
                <Select.Option key={c._id} value={c._id}>
                  {c.name}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
        </div>

        <div className={styles.field}>
          <label className={styles.label}>Type</label>
          <div className={styles.typeSelector}>
            <div
              className={`${styles.typeButton} ${type === "Debit" ? styles.selected : ""}`}
              onClick={() => setType(LEDGER_TYPE.Debit)}
            >
              <div
                className={`${styles.typeButtonCheck} ${type === "Debit" ? styles.selected : ""}`}
              >
                {type === "Debit" && <Check size={12} />}
              </div>
              <div>
                <div
                  className={`${styles.typeButtonTitle} ${type === "Debit" ? styles.selected : ""}`}
                >
                  Debit
                </div>
                <div className={styles.typeButtonDesc}>Adding to costs</div>
              </div>
            </div>
            <div
              className={`${styles.typeButton} ${type === "Credit" ? styles.selected : ""}`}
              onClick={() => setType(LEDGER_TYPE.Credit)}
            >
              <div
                className={`${styles.typeButtonCheck} ${type === "Credit" ? styles.selected : ""}`}
              >
                {type === "Credit" && <Check size={12} />}
              </div>
              <div>
                <div
                  className={`${styles.typeButtonTitle} ${type === "Credit" ? styles.selected : ""}`}
                >
                  Credit
                </div>
                <div className={styles.typeButtonDesc}>
                  Deductions from costs
                </div>
              </div>
            </div>
          </div>
        </div>

        <Form.Item
          name="category"
          label={<span className={styles.label}>Category / Type</span>}
          required
          style={{ marginBottom: 0 }}
          rules={[{ required: true, message: "Please enter a category!" }]}
        >
          <Input placeholder="Enter or select category..." />
        </Form.Item>

        <div className={styles.categoryTags}>
          <Radio.Group
            className={styles.categoryRadioGroup}
            onChange={(e) => {
              form.setFieldValue("category", e.target.value || "");
            }}
            buttonStyle="solid"
          >
            {CATEGORY_OPTIONS.map((opt) => (
              <Radio.Button key={opt} value={opt}>
                {opt}
              </Radio.Button>
            ))}
          </Radio.Group>
        </div>
        <div className={styles.costsGrid}>
          <Form.Item
            name="sellingCost"
            label={<span className={styles.label}>Selling Cost</span>}
            rules={[{ required: true, message: "Please enter a cost!" }]}
          >
            <InputNumber
              prefix="₹"
              style={{ width: "100%" }}
              placeholder="0.00"
            />
          </Form.Item>
          <Form.Item
            name="serviceCost"
            label={<span className={styles.label}>Service Cost</span>}
          >
            <InputNumber
              prefix="₹"
              style={{ width: "100%" }}
              placeholder="0.00"
            />
          </Form.Item>
          <Form.Item
            name="gstRate"
            label={<span className={styles.label}>GST (%)</span>}
            rules={[{ required: true, message: "Please enter GST rate!" }]}
          >
            <InputNumber
              suffix="%"
              style={{ width: "100%" }}
              placeholder="18"
            />
          </Form.Item>
        </div>

        <div className={styles.summary}>
          <div className={styles.summaryRow}>
            <span className={styles.summaryLabel}>
              Subtotal (Selling + Service)
            </span>
            <span className={styles.summaryValue}>
              ₹{subtotal.toLocaleString()}
            </span>
          </div>
          <div className={styles.summaryRow}>
            <span className={styles.summaryLabel}>GST Amount ({gstRate}%)</span>
            <span className={styles.summaryValue}>
              ₹{gstAmount.toLocaleString()}
            </span>
          </div>
          <div className={styles.summaryTotalRow}>
            <span className={styles.summaryTotalLabel}>Total Amount</span>
            <span
              className={`${styles.summaryTotalValue} ${type === "Credit" ? styles.credit : ""}`}
            >
              {type === "Credit" ? "- " : ""}₹{total.toLocaleString()}
            </span>
          </div>
        </div>

        <Form.Item
          name="remarks"
          label={<span className={styles.label}>Remarks</span>}
        >
          <Input.TextArea
            rows={3}
            placeholder="Add any additional notes here..."
          />
        </Form.Item>
      </Form>
    </Modal>
  );
}
